import React, { useMemo } from "react";
import { Box, Link, Typography } from "@mui/material";
import { Link as RouterLink, useParams } from "react-router-dom";
import { useKivunovaTranslation } from "@kivunova/kivufrontendcommon";
import CommonLoader from "../../../common/CommonLoader/CommonLoader";
import {
  useAcademicPrograms,
  useAllGrades,
  useAllStreams
} from "../../../../hooks/api/education/useAcademicStructure";
import { formatEntityStatus } from "../common/academicUtils";

const StreamDetail: React.FC = () => {
  const { t } = useKivunovaTranslation();
  const { streamId = "" } = useParams();
  const { data: programs = [] } = useAcademicPrograms();
  const { data: grades = [] } = useAllGrades();
  const { data: streams = [], isPending } = useAllStreams();

  const stream = useMemo(
    () => streams.find((item) => item.id === streamId),
    [streams, streamId]
  );

  const grade = useMemo(
    () => grades.find((item) => item.id === stream?.gradeId),
    [grades, stream]
  );

  const program = useMemo(
    () => programs.find((item) => item.id === grade?.programId),
    [programs, grade]
  );

  if (isPending) {
    return (
      <CommonLoader loadingMessage={t("loading-stream", "Loading stream...")} />
    );
  }

  if (!stream) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>{t("stream-not-found", "Stream not found.")}</Typography>
      </Box>
    );
  }

  return (
    <Box data-testid="stream-detail-page" sx={{ p: 3 }}>
      <Link component={RouterLink} to="..">
        {t("back-to-streams", "Back to streams")}
      </Link>

      <Typography variant="h5" component="h1" sx={{ mt: 2, mb: 0.5 }}>
        {stream.name}
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        {formatEntityStatus(stream.status)}
      </Typography>

      <Typography variant="body2" sx={{ mb: 1 }}>
        {t("grade-label", "Grade")}:{" "}
        {grade ? (
          <Link component={RouterLink} to={`../grades/${grade.id}`}>
            {grade.name}
          </Link>
        ) : (
          stream.gradeId
        )}
      </Typography>
      <Typography variant="body2">
        {t("program-label", "Program")}:{" "}
        {program ? (
          <Link component={RouterLink} to={`../programs/${program.id}`}>
            {program.name}
          </Link>
        ) : (
          (grade?.programId ?? "—")
        )}
      </Typography>
    </Box>
  );
};

export default StreamDetail;
